import { AccessToken, OktaAuth } from '@okta/okta-auth-js';

/**
 * Single shared OktaAuth instance used both by the React `<Security>` provider
 * and by the axios layer (`Http.ts`). Keeping one instance means the axios
 * interceptors read/renew the exact same token the app is using.
 *
 * `autoRenew` refreshes the access token in the background before it expires,
 * and `expireEarlySeconds` treats the token as expired slightly ahead of time
 * so a request never goes out with a token that is about to lapse.
 */
export const oktaAuth = new OktaAuth({
  issuer: `https://${process.env.REACT_APP_OKTA_BASE_URL}/oauth2/default`,
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: window.location.origin + '/login/callback',
  tokenManager: {
    autoRenew: true,
    expireEarlySeconds: 60,
  },
});

/**
 * Returns a valid access token string, renewing it first if it has expired
 * (or is about to). Returns `null` when there is no token or renewal fails,
 * so callers can decide how to handle an unauthenticated request.
 */
export async function getValidAccessToken(): Promise<string | null> {
  try {
    let accessToken = (await oktaAuth.tokenManager.get('accessToken')) as AccessToken | undefined;
    if (!accessToken) {
      return null;
    }
    if (oktaAuth.tokenManager.hasExpired(accessToken)) {
      accessToken = (await oktaAuth.tokenManager.renew('accessToken')) as AccessToken | undefined;
    }
    return accessToken?.accessToken ?? null;
  } catch {
    return null;
  }
}
